/**
 * The cost trace, as data.
 *
 * `buildLineTrace` and `buildJobTrace` turn a backend calculation result into
 * an ordered list of node descriptors. No JSX, no component state — the whole
 * mapping from backend key to "what the estimator reads" lives here so it can
 * be unit-tested against fixture payloads.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE MONEY RULE
 * The backend owns every price. This module reads dollars; it never derives
 * them. Where the backend reports a total for the whole line, that total is
 * what is shown — per-unit dollars times the line quantity is a fallback for
 * payloads carrying no line total, because the per-unit figures are already
 * rounded to the cent and extending one re-scales the rounding error into a
 * line that visibly disagrees with the job total (see qboLine.getLineAmount).
 * Nothing here subtracts one dollar figure from another to invent a third, and
 * nothing computes a margin percentage.
 *
 * THE FINISH RULE
 * On rate-card lines the backend folds finish INTO material_cost_per_unit
 * (see costing_service.compute_rate_card_line). So Finish is a CHILD of
 * Material, never a sibling — a sibling would double count the job.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Node descriptor:
 *   { id, kind?, label, value, valueKind, valueSuffix?, tone?,
 *     formula?: string[], rows?: [{label,value,valueKind}],
 *     table?: {head:[], rows:[[]]}, source?: [{label,value,hint}],
 *     badges?: [{text,tone}], warnings?: [{text,tone}], actions?: [{id,label,hint}],
 *     note?, qbo?, children?: [node], defaultOpen?,
 *     secondary?: { label?, rows?, table?, source? } }
 *
 * `secondary` is the same rows/table/source shapes, rendered behind one more
 * click inside the node — provenance the estimator can prove but does not
 * have to read. A node whose only content is `secondary` is still expandable.
 * Emit it only when it HAS content: TraceNode counts any truthy `secondary`
 * as making the node expandable, so `{ source: [] }` opens onto nothing.
 */

import {
  formatCurrency,
  formatThicknessInches,
} from "../../../utils/formatters";
import { getPricingTierLabel } from "../../../utils/pricingTemplates";
import {
  AI_DRAFT_TRACE_NOTE,
  DEPARTMENT_LABELS,
  DEPARTMENT_ORDER,
  FINISH_INSIDE_MATERIAL_NOTE,
  FINISH_MODE_NONE,
  FINISH_MODE_TEXT,
  FINISH_RATE_MISSING_WARNING,
  JOB_LABELS,
  LABOR_BASIS_WORDS,
  LABOR_CARD_MISSING_WARNING,
  LABOR_FLOOR_NOTE,
  LABOR_OVERRIDE_CLEAR_HINT,
  LABOR_OVERRIDE_LEAD,
  LABOR_OVERRIDE_CARD_MISSING_WARNING,
  LABOR_RATE_TYPE_LABELS,
  MATERIAL_AMBIGUOUS_WARNING,
  MATERIAL_INDEPENDENT_NOTE,
  PROFIT_CLAMPED_WARNING,
  PROFIT_ON_PRICE_NOTE,
  QBO_PASTE_HEADING,
  QUOTED_PRICE_BADGE,
  QUOTED_PRICE_DEFAULT_HINT,
  STANDALONE_QUOTED_LINE_BADGE,
  STANDALONE_QUOTED_LINE_NOTE,
  THICKNESS_FROM_CARD_NOTE,
  ZERO_LABOR_TRACE_NOTE,
  ZERO_LABOR_TRACE_WARNING,
  NEGATIVE_LABOR_OVERRIDE_WARNING,
  describeQuotedPriceBasis,
  describeDelivery,
  describeFallbackReason,
  describeInstall,
  describeMaterialResolution,
  describeQuotedPriceFormula,
  hasZeroLaborOverride,
  hasNegativeLaborOverride,
  laborOverrideApplied,
  materialFailureTraceNote,
  passThroughNote,
} from "./traceLabels";
import { describeLineError, describeMaterialError } from "./lineErrors";
import { buildChildComponentsNode } from "./childTrace";
import { LINKED_TIER_LABELS, LINKED_TIER_ORDER } from "./rafterTailModel";
import {
  buildQboJobTsv,
  buildQboLineDescription,
  extendPerUnit,
  getEngineLineTotal,
  getFinalUnitPrice,
  getItemQuantity,
  getLineAmount,
  getLineLaborHours,
  getQboUnitCostString,
  toNumberOrNull,
} from "./qboLine";

/* ── small numeric helpers (display only) ──────────────────────────────── */

const num = toNumberOrNull;

/** First finite value in the list, else null. */
function pick(...values) {
  for (const value of values) {
    const parsed = num(value);
    if (parsed !== null) return parsed;
  }
  return null;
}

/** Trim trailing zeros so 0.1280 reads 0.128 and 8.00 reads 8. */
function trim(value, maxDigits = 4) {
  const parsed = num(value);
  if (parsed === null) return null;
  return String(Number(parsed.toFixed(maxDigits)));
}

function area(value) {
  const parsed = num(value);
  return parsed === null ? null : parsed.toFixed(2);
}

function percent(ratio) {
  const parsed = num(ratio);
  if (parsed === null) return null;
  return String(Number((parsed * 100).toFixed(2)));
}

function has(value) {
  return value !== null && value !== undefined && value !== "";
}

function compact(list) {
  return list.filter(Boolean);
}

/* ── rate-card confirmation age ────────────────────────────────────────────
   `rate_confirmed_at` is when the owner last signed off on a card's rates. It
   is the difference between "these numbers were checked" and "these numbers
   have been sitting here for a year", so the trace says which, in words rather
   than an ISO stamp nobody reads.                                            */

const RATE_NEVER_CONFIRMED = "rates not yet signed off";

const RELATIVE_UNITS = [
  { limit: 365, unit: "year", days: 365 },
  { limit: 30, unit: "month", days: 30 },
  { limit: 7, unit: "week", days: 7 },
  { limit: 1, unit: "day", days: 1 },
];

/**
 * "rates signed off 2 days ago" / "rates not yet signed off" — deliberately NOT
 * the word "confirmed", which is already taken by card_status and would read as
 * "confirmed · not yet confirmed" when joined with it.
 *
 * @param value ISO timestamp from the backend, or null
 * @param now   injectable clock so the test is not time-dependent
 */
export function describeRateConfirmation(value, now = new Date()) {
  if (!value) return RATE_NEVER_CONFIRMED;
  const confirmed = new Date(value);
  if (Number.isNaN(confirmed.getTime())) return RATE_NEVER_CONFIRMED;

  const days = Math.floor((now.getTime() - confirmed.getTime()) / 86400000);
  if (days < 0) return "rates signed off just now";
  if (days < 1) return "rates signed off today";

  for (const { limit, unit, days: per } of RELATIVE_UNITS) {
    if (days >= limit) {
      const count = Math.floor(days / per);
      return `rates signed off ${count} ${unit}${count === 1 ? "" : "s"} ago`;
    }
  }
  return "rates signed off today";
}

/** A row is worth showing when the backend gave us something for it. */
function row(label, value, valueKind = "currency", extra = {}) {
  if (!has(value)) return null;
  return { label, value, valueKind, ...extra };
}

/* ═══ LINE SCOPE ═══════════════════════════════════════════════════════════ */

/**
 * @param item  results.line_item_results[i]
 * @param results the whole calculation payload (job-level rates live there)
 * @param formData the form state (tier, margins, labor rate type)
 * @param finishRatesById  id → finish rate record from GET /api/rate-cards
 */
export function buildLineTrace(
  item,
  results = {},
  formData = {},
  finishRatesById = {},
  lineIndex = 0,
) {
  if (!item) return [];
  const components = item.components || {};
  const quantity = getItemQuantity(item);
  // Provenance, not a price: when a model proposed this line's inputs, the two
  // nodes it proposed inputs FOR say so. The dollars are still the engine's.
  const aiDrafted = Boolean(formData?.line_items?.[lineIndex]?.ai_drafted);

  if (item.linked_pricing) {
    return compact([
      buildLineCostNode(item, components, quantity),
      buildLinkedPriceNode(item),
      buildCustomerPriceNode(item, quantity, lineIndex),
    ]);
  }

  return compact([
    buildProvenanceNode(item, components),
    buildMaterialNode(item, components, quantity, finishRatesById, aiDrafted),
    buildLaborNode(item, components, results, formData, quantity, aiDrafted),
    buildChildComponentsNode(item, finishRatesById),
    buildLineCostNode(item, components, quantity),
    buildMarkupNode(item, components, results, formData),
    buildCustomerPriceNode(item, quantity, lineIndex),
  ]);
}

/**
 * The strip above the nodes. It exists only to carry a fallback warning:
 * which engine and geometry priced the line is already the editor's subtitle,
 * built from the same two helpers, and printing it again said nothing new.
 */
function buildProvenanceNode(item, components) {
  const fallback = describeFallbackReason(
    item.material_cost_mode_fallback_reason ||
      components.material_cost_mode_fallback_reason,
  );

  if (!fallback) return null;

  return {
    id: "provenance",
    kind: "provenance",
    warnings: [{ text: fallback, tone: "warn" }],
  };
}

/**
 * MATERIAL — the line's material total, with finish nested inside it.
 * Value is the backend's own line total; the per-piece figure is a detail row.
 */
function buildMaterialNode(
  item,
  components,
  quantity,
  finishRatesById,
  aiDrafted = false,
) {
  const laborOverridden = laborOverrideApplied(item.input_data);
  const override =
    item.material_price_override?.applied === true
      ? item.material_price_override
      : null;
  const materialError = item.material_error || null;

  const perUnit = pick(
    components.true_material_cost_per_unit,
    item.material_cost_per_unit,
  );
  // The engine's own line total wins. Its per-unit figure is rounded to the
  // cent, so extending it would show a line that does not tie to the job.
  const lineTotal = materialError
    ? 0
    : (getEngineLineTotal(item, "total_material_cost") ??
      extendPerUnit(perUnit, quantity));

  const sqft = num(item.calculated_sqft);
  const rate = pick(
    item.material_rate_used_per_sqft,
    components.material_rate_used_per_sqft,
  );

  const formula = [];
  const quotedFormula = override
    ? describeQuotedPriceFormula(override, item.calculated_sqft, formatCurrency)
    : null;
  if (quotedFormula) {
    formula.push(quotedFormula);
  } else if (sqft !== null && rate !== null) {
    let sentence = `${area(sqft)} sq ft × ${formatCurrency(rate)}/sq ft`;
    if (has(item.waste_pct_applied)) {
      sentence += ` × (1 + ${trim(item.waste_pct_applied, 2)}% waste)`;
    }
    const multiplier = pick(
      components.product_based_multiplier_used,
      item.product_based_multiplier_used,
    );
    if (multiplier !== null && multiplier !== 1) {
      sentence += ` × ${trim(multiplier)}`;
    }
    formula.push(sentence);
  }
  if (!override && perUnit !== null && quantity > 1) {
    formula.push(
      `${formatCurrency(perUnit)} per piece × ${trim(quantity)} pieces`,
    );
  }

  const resolution = item.material_resolution || null;
  const resolutionText = describeMaterialResolution(resolution);
  const defaultedTieBreaks = Array.isArray(resolution?.defaulted_tie_breaks)
    ? resolution.defaulted_tie_breaks
    : [];
  const defaultedParts = compact([
    defaultedTieBreaks.length
      ? formatThicknessInches(resolution?.thickness_in).replace("″", '"')
      : null,
    defaultedTieBreaks.includes("sanded") ? "sanded" : null,
    defaultedTieBreaks.includes("sheet_4x12") ? "4x12" : null,
  ]);
  const defaultedText = defaultedParts.length
    ? `defaulted: ${defaultedParts.join(" ")} — change on the line`
    : null;
  const thickness = resolution ? num(resolution.thickness_in) : null;
  const thicknessText =
    thickness === null
      ? null
      : `${trim(thickness)}"${
          resolution && resolution.thickness_defaulted
            ? ` ${THICKNESS_FROM_CARD_NOTE}`
            : ""
        }`;

  // A quoted price is the reason this line costs what it costs — it stays at
  // the top level, next to the figure it replaced.
  const quotedSource = compact([
    override
      ? {
          label: "Quoted price",
          value: formatCurrency(override.amount),
          valueKind: "text",
          hint: override.note || QUOTED_PRICE_DEFAULT_HINT,
        }
      : null,
    // Which unit the quoted amount is in — "$14.50" alone is unreadable when
    // the basis is per lineal foot or per gallon.
    override && describeQuotedPriceBasis(override.basis)
      ? {
          label: "Quoted basis",
          value: describeQuotedPriceBasis(override.basis),
          valueKind: "text",
        }
      : null,
    override && has(override.catalog_cost_per_sqft)
      ? {
          label: "Catalog rate (not used)",
          value: `${formatCurrency(override.catalog_cost_per_sqft)} per sq ft`,
          valueKind: "text",
        }
      : null,
  ]);

  // Which record, at which price, at which thickness. All of it restates the
  // two selects the estimator just used, so it lives one click away.
  const provenance = compact([
    item.material_name_used
      ? {
          label: "Material record",
          value: item.material_name_used,
          hint: resolutionText,
        }
      : resolutionText
        ? {
            label: "Material record",
            value: "Not resolved",
            hint: resolutionText,
          }
        : null,
    has(item.cost_per_sqft_used)
      ? {
          label: "Sheet price",
          value: `${formatCurrency(item.cost_per_sqft_used)} per sq ft`,
        }
      : null,
    thicknessText ? { label: "Thickness", value: thicknessText } : null,
    defaultedText
      ? { label: "Automatic selection", value: defaultedText }
      : null,
  ]);

  // A raw Marshmallow payload is never shown; it goes behind `detail`.
  const lineError = describeLineError(item.error);
  const materialErrorText = materialError
    ? describeMaterialError(materialError).message
    : null;
  const warnings = compact([
    materialErrorText ? { text: materialErrorText, tone: "error" } : null,
    !materialError && item.material_ambiguous
      ? { text: MATERIAL_AMBIGUOUS_WARNING, tone: "error" }
      : null,
    lineError.message
      ? { text: lineError.message, tone: "error", detail: lineError.detail }
      : null,
  ]);

  // The backend says so when it priced this line from the quoted price alone —
  // no product type, so no rate card and no material catalog were consulted.
  const standaloneQuoted = item.standalone_quoted_line === true;

  return {
    id: "material",
    label: "Material",
    value: lineTotal,
    valueKind: "currency",
    tone: "material",
    defaultOpen: true,
    badges: compact([
      standaloneQuoted
        ? { text: STANDALONE_QUOTED_LINE_BADGE, tone: "quoted" }
        : override
          ? { text: QUOTED_PRICE_BADGE, tone: "quoted" }
          : null,
      aiDrafted ? { text: "AI-drafted", tone: "info" } : null,
    ]),
    note: materialError
      ? materialFailureTraceNote(laborOverridden)
      : standaloneQuoted
        ? STANDALONE_QUOTED_LINE_NOTE
        : aiDrafted
          ? AI_DRAFT_TRACE_NOTE
          : null,
    formula,
    rows: compact([
      row("Per piece", perUnit, "currency"),
      row("Pieces", quantity, "number"),
    ]),
    source: quotedSource,
    secondary: provenance.length
      ? { label: "Where this came from", source: provenance }
      : undefined,
    warnings,
    children: compact([buildFinishNode(item, finishRatesById)]),
  };
}

/**
 * FINISH — a child of Material. The backend already folded it into the
 * material figure, so this node explains a slice, it does not add one.
 */
function buildFinishNode(item, finishRatesById) {
  const finishCost = num(item.finish_cost_per_unit);
  const mode = item.finish_mode_applied;
  const rateUsed = item.finish_rate_used || null;
  const missing = Boolean(item.finish_rate_missing);

  if (finishCost === null && !mode && !missing) return null;

  const modeText = mode ? FINISH_MODE_TEXT[mode] || mode : FINISH_MODE_NONE;

  const formula = [];
  const catalogRate =
    rateUsed && finishRatesById ? finishRatesById[rateUsed.id] : null;
  const coats = catalogRate ? num(catalogRate.coats) : null;
  const perGallon = catalogRate ? num(catalogRate.cost_per_gallon) : null;
  const coverage = catalogRate
    ? num(catalogRate.coverage_sqft_per_gal_per_coat)
    : null;
  const perSqFt = rateUsed ? num(rateUsed.cost_per_sqft) : null;

  if (coats !== null && perGallon !== null && coverage !== null) {
    formula.push(
      `${trim(coats)} coats × ${formatCurrency(perGallon)}/gal ÷ ${trim(
        coverage,
      )} sq ft per gallon per coat = ${formatCurrency(perSqFt ?? catalogRate.cost_per_sqft)}/sq ft`,
    );
  } else if (perSqFt !== null) {
    // Never fabricate coats or coverage we were not given.
    formula.push(`${formatCurrency(perSqFt)}/sq ft`);
  }

  return {
    id: "finish",
    label: "Of which finish",
    value: finishCost,
    valueKind: "currency",
    valueSuffix: "per piece",
    tone: "finish",
    note: FINISH_INSIDE_MATERIAL_NOTE,
    formula,
    rows: compact([
      { label: "Finish", value: modeText, valueKind: "text" },
      rateUsed && rateUsed.name
        ? { label: "Finish rate", value: rateUsed.name, valueKind: "text" }
        : null,
    ]),
    warnings: missing
      ? [{ text: FINISH_RATE_MISSING_WARNING, tone: "warn" }]
      : [],
  };
}

/** LABOR — shop time, its rate card, and any hand-entered override. */
function buildLaborNode(
  item,
  components,
  results,
  formData,
  quantity,
  aiDrafted = false,
) {
  const perUnit = pick(
    components.true_labor_cost_per_unit,
    item.true_labor_cost_per_unit,
  );
  // Same rule as material: the engine's line total, not per-piece x pieces.
  const lineTotal =
    getEngineLineTotal(item, "calculated_labor_cost") ??
    extendPerUnit(perUnit, quantity);

  const isOverride = laborOverrideApplied(item.input_data);
  const hourlyRate = num(results.fab_labor_rate_M68);
  const basis = item.labor_basis_used || null;
  const basisWord = basis ? LABOR_BASIS_WORDS[basis] || basis : null;
  const laborUnits = num(item.labor_units);
  const rates = item.labor_per_sqft_rates_used || null;

  const departmentHours = DEPARTMENT_ORDER.map((key) => ({
    key,
    label: DEPARTMENT_LABELS[key],
    rate: rates ? num(rates[key]) : null,
    hours: num(item[`${key}_hours_per_unit`]),
  }));

  // Hours are not money — summing them is arithmetic the trace is allowed to
  // do, and the backend's own per-unit total is preferred when present.
  const ratesSum = rates
    ? DEPARTMENT_ORDER.reduce((total, key) => total + (num(rates[key]) || 0), 0)
    : null;
  const hoursPerPiece =
    pick(item.labor_estimate_hours) ??
    departmentHours.reduce(
      (total, dept) => (dept.hours === null ? total : total + dept.hours),
      0,
    );

  const formula = [];
  if (!isOverride && laborUnits !== null && basisWord && ratesSum !== null) {
    formula.push(
      `${trim(laborUnits)} ${basisWord} × ${trim(
        ratesSum,
      )} hr per ${basisWord} = ${trim(hoursPerPiece)} hr per piece`,
    );
  }
  // Line hours come from what was entered for the line when the line was
  // hand-entered; only a rate-card line extends the (4-dp) per-piece figure.
  // Stating the engine's own line total as the result keeps the sentence from
  // arriving at a number a cent or two off the one printed above it.
  const lineHours = getLineLaborHours(item);
  if (lineHours !== null && hourlyRate !== null) {
    formula.push(
      `${trim(lineHours)} hr for the line × ${formatCurrency(hourlyRate)}/hr${
        lineTotal === null ? "" : ` = ${formatCurrency(lineTotal)}`
      }`,
    );
  } else if (hoursPerPiece !== null && hourlyRate !== null) {
    formula.push(
      `${trim(hoursPerPiece)} hr per piece × ${trim(
        quantity,
      )} pieces × ${formatCurrency(hourlyRate)}/hr`,
    );
  }

  const table = isOverride
    ? {
        head: ["Department", "Entered for the line", "Per piece"],
        rows: DEPARTMENT_ORDER.map((key) => {
          const enteredField =
            key === "paint" ? "finish_hours" : `${key}_hours`;
          const entered = num(item.input_data?.[enteredField]);
          const perPiece = num(item[`${key}_hours_per_unit`]);
          return [
            DEPARTMENT_LABELS[key],
            entered === null ? "—" : `${trim(entered)} hr`,
            perPiece === null ? "—" : `${trim(perPiece)} hr`,
          ];
        }),
      }
    : rates
      ? {
          head: [
            "Department",
            basisWord ? `hr per ${basisWord}` : "hr per unit",
            "Hours per piece",
          ],
          rows: departmentHours.map((dept) => [
            dept.label,
            dept.rate === null ? "—" : trim(dept.rate, 5),
            dept.hours === null ? "—" : `${trim(dept.hours)} hr`,
          ]),
        }
      : null;

  const card = item.rate_card_resolved || null;
  const cardMissing = Boolean(
    card?.labor_card_missing ||
    item.material_cost_mode_fallback_reason === "no_matching_rate_card",
  );

  const cardLabelParts = compact([
    card?.category || null,
    card?.card_id ? `card #${card.card_id}` : null,
    card?.card_status || null,
  ]);

  // How stale the card's rates are is part of its provenance, not a footnote.
  const confirmation = card
    ? describeRateConfirmation(card.rate_confirmed_at)
    : null;

  const source = compact([
    card && !cardMissing
      ? {
          // Say plainly that an overridden line bypassed the card it matched.
          label: isOverride ? "Rate card (not used)" : "Rate card",
          value: cardLabelParts.join(" · ") || "Rate card",
          hint: compact([
            confirmation,
            card.material_independent
              ? MATERIAL_INDEPENDENT_NOTE(card.category)
              : null,
          ]).join(" · "),
        }
      : null,
    hourlyRate !== null
      ? {
          label:
            LABOR_RATE_TYPE_LABELS[formData?.laborRateType] ||
            LABOR_RATE_TYPE_LABELS.burden,
          value: `${formatCurrency(hourlyRate)}/hr`,
          hint: "from pricing settings",
        }
      : null,
  ]);

  // A hand-typed all-zero override really does price the line with no shop
  // labor. Unconfirmed it is the loudest thing on this node; confirmed it stays
  // stated, quietly, because a $0 labor line must never look like an accident.
  const zeroLabor = hasZeroLaborOverride(item.input_data);
  const zeroLaborConfirmed = Boolean(item.input_data?.zero_labor_confirmed);
  const hasNegativeLabor = hasNegativeLaborOverride(item.input_data);

  const warnings = compact([
    hasNegativeLabor
      ? { text: NEGATIVE_LABOR_OVERRIDE_WARNING, tone: "error" }
      : null,
    zeroLabor && !zeroLaborConfirmed
      ? { text: ZERO_LABOR_TRACE_WARNING, tone: "error" }
      : null,
    cardMissing
      ? {
          text: isOverride
            ? LABOR_OVERRIDE_CARD_MISSING_WARNING
            : LABOR_CARD_MISSING_WARNING,
          tone: "error",
        }
      : null,
    item.labor_floor_applied
      ? { text: LABOR_FLOOR_NOTE(trim(item.labor_floor_hours)), tone: "warn" }
      : null,
  ]);

  const actions = isOverride
    ? [
        {
          id: "clear-labor-override",
          label: "Clear override",
          hint: LABOR_OVERRIDE_CLEAR_HINT,
        },
        { id: "focus-labor-override", label: "Edit hours" },
      ]
    : [{ id: "focus-labor-override", label: "Override hours for this line" }];

  return {
    id: "labor",
    label: "Shop labor",
    value: lineTotal,
    valueKind: "currency",
    tone: "labor",
    defaultOpen: true,
    // Both can be true at once — a model drafted the hours and a human then
    // typed over them. Override reads first because it is the stronger claim.
    badges: compact([
      isOverride ? { text: "Override", tone: "warn" } : null,
      aiDrafted ? { text: "AI-drafted", tone: "info" } : null,
    ]),
    note:
      zeroLabor && zeroLaborConfirmed && !hasNegativeLabor
        ? ZERO_LABOR_TRACE_NOTE
        : isOverride
          ? LABOR_OVERRIDE_LEAD
          : aiDrafted
            ? AI_DRAFT_TRACE_NOTE
            : null,
    formula,
    rows: compact([
      row("Per piece", perUnit, "currency"),
      row("Hours for the line", lineHours, "hours"),
      row("Hours per piece", hoursPerPiece, "hours"),
    ]),
    // An overridden line's table has no twin — the hours it shows were typed
    // for THIS line and nothing else in the workbench states them. The
    // rate-card table is the editor's read-out verbatim, so it goes behind the
    // click, together with the card that produced it.
    table: isOverride ? table : null,
    source: isOverride ? source : [],
    secondary:
      !isOverride && (table || source.length)
        ? {
            label: "Where this came from",
            table,
            source,
          }
        : undefined,
    warnings,
    actions,
  };
}

/** LINE COST — what the shop spends, before anything is added on. */
function buildLineCostNode(item, components, quantity) {
  const total = pick(item.calculated_total_cost);
  const material = pick(item.total_material_cost);
  const labor = pick(item.calculated_labor_cost);
  if (total === null && material === null && labor === null) return null;

  return {
    id: "line-cost",
    label: "Line cost",
    value: total,
    valueKind: "currency",
    tone: "total",
    rows: compact([
      row("Material and finish", material, "currency"),
      row("Shop labor", labor, "currency"),
    ]),
  };
}

/**
 * LINKED TIER PRICE — the rafter-tail trial. D is the only place overhead,
 * profit and commission are applied; every tier is derived from D. Shown as
 * the ladder itself so the estimator sees where this quote sits on it.
 */
function buildLinkedPriceNode(item) {
  const linked = item.linked_pricing;
  const option = linked?.options?.[linked.selected_option];
  if (!option) return null;
  const breakdown = option.distributor_breakdown || {};
  const rates = linked.distributor_rates_used || {};
  const commissionPct =
    Number(rates.commission_rate || 0) +
    Number(rates.shop_commission_rate || 0);
  const tierRows = LINKED_TIER_ORDER.map((tier) =>
    row(
      `${LINKED_TIER_LABELS[tier]}${tier === linked.tier ? " (this estimate)" : ""}`,
      option.tier_prices?.[tier]?.total,
      "currency",
    ),
  );

  return {
    id: "linked-price",
    label: "Price from the trial ladder",
    value: pick(linked.price_total),
    valueKind: "currency",
    tone: "neutral",
    formula: [
      `Distributor = cost + ${trim(rates.overhead_ratio, 2)}% overhead, then a ${trim(
        rates.profit_margin,
        2,
      )}% margin on price, then ${trim(commissionPct, 2)}% commission.`,
      "Dealer = Distributor ÷ 0.80 · Retail = Dealer ÷ 0.60 · Contractor = Retail less 20%.",
      "Overhead, profit and commission are inside Distributor — never added again. Tax is added on the job.",
    ],
    rows: compact(tierRows),
    // What D is made of — provenance for the ladder, not more price.
    source: compact([
      row("Overhead inside Distributor", breakdown.overhead, "currency"),
      row("Profit inside Distributor", breakdown.profit, "currency"),
      row(
        "Sales commission inside Distributor",
        breakdown.sales_commission,
        "currency",
      ),
      row(
        "Shop profit sharing inside Distributor",
        breakdown.shop_commission,
        "currency",
      ),
    ]),
  };
}

/** WHAT GETS ADDED ON — overhead, profit and commission, per piece. */
function buildMarkupNode(item, components, results, formData) {
  const rows = compact([
    row("Overhead", components.overhead_amount_per_unit, "currency"),
    row("Profit", components.profit_amount_per_unit, "currency"),
    row(
      "Sales commission",
      components.sales_commission_amount_per_unit,
      "currency",
    ),
    row(
      "Shop profit sharing",
      components.shop_commission_profit_sharing_amount_per_unit,
      "currency",
    ),
  ]);
  if (rows.length === 0) return null;

  // Same authority rule as buildJobTrace: the rates the backend actually used
  // win over whatever the form is showing right now.
  const overheadPct =
    percent(results.overhead_ratio_used) ?? trim(formData?.overhead_ratio, 2);
  const profitPct =
    percent(results.profit_margin_used) ?? trim(formData?.profit_margin, 2);
  const commissionPct = trim(formData?.commission_rate, 2);

  const formula = [];
  if (overheadPct && profitPct) {
    formula.push(
      `Overhead at ${overheadPct}% of cost, then profit at ${profitPct}% of the final price${
        commissionPct ? `, then commission at ${commissionPct}%` : ""
      }.`,
    );
  }
  if (results.profit_basis_used === "margin_on_price") {
    formula.push(PROFIT_ON_PRICE_NOTE);
  }

  const tier =
    formData?.client_pricing_tier || formData?.default_client_pricing_tier;

  return {
    id: "markup",
    label: "What gets added on",
    value: null,
    valueKind: null,
    tone: "neutral",
    note: "Per piece.",
    formula,
    rows,
    source: tier
      ? [
          {
            label: "Pricing tier",
            value: getPricingTierLabel(tier),
            valueKind: "text",
            hint: "Every rate above comes from this tier's template.",
          },
        ]
      : [],
    actions: [{ id: "open-tier", label: "Change tier" }],
  };
}

/** CUSTOMER PRICE — the quoted number, plus the QuickBooks copy-out. */
function buildCustomerPriceNode(item, quantity, lineIndex = 0) {
  const unitPrice = getFinalUnitPrice(item);
  const amount = getLineAmount(item);

  const lineName =
    typeof item?.input_data?.name === "string"
      ? item.input_data.name.trim()
      : "";
  const lineDescription =
    typeof item?.input_data?.description === "string"
      ? item.input_data.description.trim()
      : "";

  return {
    id: "customer-price",
    label: "Customer price",
    value: amount,
    valueKind: "currency",
    tone: "accent",
    defaultOpen: true,
    rows: compact([
      row("Per piece", unitPrice, "currency"),
      row("Quantity", quantity, "number"),
    ]),
    source: compact([
      lineName
        ? { label: "Line name", value: lineName, valueKind: "text" }
        : null,
      lineDescription
        ? { label: "Description", value: lineDescription, valueKind: "text" }
        : null,
    ]),
    qbo: {
      description: buildQboLineDescription(item, lineIndex),
      unitPrice: getQboUnitCostString(item),
    },
  };
}

/* ═══ JOB SCOPE ════════════════════════════════════════════════════════════ */

/** The whole-job trace. Every spreadsheet cell address dies here. */
export function buildJobTrace(results, formData = {}) {
  if (!results) return [];

  // Prefer the *_used keys: they are the rates that produced these dollars.
  // Form state is only a fallback for payloads predating those keys — after an
  // edit it describes the next run, not this one.
  const overheadPct =
    percent(results.overhead_ratio_used) ?? trim(formData?.overhead_ratio, 2);
  const profitPct =
    percent(results.profit_margin_used) ?? trim(formData?.profit_margin, 2);
  const taxPct =
    percent(results.material_sales_tax_rate_used) ??
    trim(formData?.material_sales_tax_rate, 2);
  const linked = results.linked_pricing_summary || null;
  const linkedNote = (amount) =>
    linked && has(amount)
      ? `Includes ${formatCurrency(amount)} already inside the rafter-tail trial prices.`
      : null;
  const jobRatesBase = linked ? linked.job_rates_cost : null;
  const shopHours = num(results.total_estimated_fab_hours);
  const hourlyRate = num(results.fab_labor_rate_M68);

  const buildCostChildren = compact([
    node(
      "materials",
      JOB_LABELS.materials,
      results.total_calculated_material_cost,
      {
        tone: "material",
      },
    ),
    node(
      "material-origination",
      JOB_LABELS.materialOrigination,
      results.material_origination_cost_P48,
    ),
    node(
      "material-financing",
      JOB_LABELS.materialFinancing,
      results.financing_percent_cost_P50,
    ),
    node(
      "materials-after-financing",
      JOB_LABELS.materialsAfterFinancing,
      results.total_material_cost_plus_financing_P52,
    ),
    node(
      "shop-labor",
      JOB_LABELS.shopLabor,
      results.total_fab_burden_cost_P68,
      {
        tone: "labor",
        // With trial lines, P68 is the job-rate hours at the rate plus the
        // trial lines' own labor (already in cents), so the formula says so
        // and still reproduces the figure beside it.
        formula:
          shopHours === null || hourlyRate === null
            ? []
            : linked
              ? [
                  `${trim(num(linked.job_rate_hours))} hr × ${formatCurrency(
                    hourlyRate,
                  )}/hr`,
                  `+ ${formatCurrency(linked.trial_labor)} rafter-tail trial labor (${trim(
                    num(linked.trial_hours),
                  )} hr)`,
                ]
              : [`${trim(shopHours)} hr × ${formatCurrency(hourlyRate)}/hr`],
      },
    ),
    node(
      "installation",
      JOB_LABELS.installation,
      results.total_install_cost_P72,
      {
        tone: "passthrough",
        note: describeInstall(results),
      },
    ),
    node("delivery", JOB_LABELS.delivery, results.total_delivery_cost_P79, {
      tone: "passthrough",
      note: describeDelivery(results),
    }),
    node("misc", JOB_LABELS.miscellaneous, results.total_misc_costs_P87, {
      tone: "passthrough",
    }),
  ]);

  const passThrough = num(results.pass_through_costs);

  return compact([
    node("build-cost", JOB_LABELS.buildCost, results.total_job_costs_P91, {
      tone: "total",
      defaultOpen: true,
      children: buildCostChildren,
    }),
    node("overhead", JOB_LABELS.overhead, results.overhead_P93, {
      formula: compact([
        overheadPct
          ? linked
            ? `${formatCurrency(jobRatesBase)} not on the trial × ${overheadPct}%`
            : `${JOB_LABELS.buildCost} × ${overheadPct}%`
          : null,
        linkedNote(linked?.overhead),
      ]),
    }),
    node("breakeven", JOB_LABELS.breakeven, results.breakeven_P95),
    node("profit", JOB_LABELS.profit, results.profit_P97, {
      formula: compact([
        profitPct
          ? linked
            ? `${profitPct}% of the final price on everything not on the trial`
            : `${profitPct}% of the final price`
          : null,
        PROFIT_ON_PRICE_NOTE,
        linkedNote(linked?.profit),
      ]),
      warnings:
        results.profit_margin_clamped === true
          ? [{ text: PROFIT_CLAMPED_WARNING, tone: "error" }]
          : [],
    }),
    node("sales-tax", JOB_LABELS.salesTax, results.sales_tax_P99, {
      formula: has(results.sales_tax_P99_base)
        ? [
            `on ${formatCurrency(
              results.sales_tax_P99_base,
            )} of materials plus shop labor${taxPct ? ` at ${taxPct}%` : ""}`,
          ]
        : [],
      rows: compact([
        row(JOB_LABELS.taxedAmount, results.sales_tax_P99_base, "currency"),
      ]),
    }),
    node(
      "price-before-commission",
      JOB_LABELS.priceBeforeCommission,
      results.sale_price_no_comm_P101,
    ),
    node("commissions", JOB_LABELS.commissions, results.commissions_P103, {
      formula: compact([linkedNote(linked?.commissions)]),
      children: compact([
        node(
          "sales-commission",
          JOB_LABELS.salesCommission,
          results.sales_commissions_P103,
        ),
        node(
          "shop-profit-sharing",
          JOB_LABELS.shopProfitSharing,
          results.shop_commission_profit_sharing_P103,
        ),
      ]),
    }),
    node("quote", JOB_LABELS.quote, results.final_suggested_sale_price_P105, {
      tone: "accent",
      defaultOpen: true,
    }),
    passThrough === null
      ? null
      : {
          id: "pass-through",
          kind: "note",
          label: JOB_LABELS.passThrough,
          value: passThrough,
          valueKind: "currency",
          tone: "passthrough",
          note: passThroughNote(formatCurrency(passThrough)),
        },
    buildQboExportNode(results),
  ]);
}

/**
 * The whole job as QuickBooks rows — the last thing an estimator does with a
 * finished quote, so it sits at the bottom of the job trace.
 *
 * It carries no dollar figure of its own: the money above it is the money in
 * the rows. When nothing priced there is nothing to paste, and the node drops
 * out entirely rather than offering a button that copies an empty string.
 */
function buildQboExportNode(results) {
  const tsv = buildQboJobTsv(results?.line_item_results);
  if (!tsv) return null;
  return {
    id: "qbo-export",
    label: QBO_PASTE_HEADING,
    value: null,
    tone: "neutral",
    qbo: { tsv, lineCount: tsv.split("\n").length },
  };
}

/** Job-scope node factory — drops the node entirely when the key is absent. */
function node(id, label, value, extra = {}) {
  if (!has(value)) return null;
  return {
    id,
    label,
    value,
    valueKind: "currency",
    tone: "neutral",
    ...extra,
  };
}

/* ── traversal helper (used by tests and by the inspector) ─────────────── */

export function flattenTrace(nodes = []) {
  return nodes.reduce(
    (flat, current) => [...flat, current, ...flattenTrace(current.children)],
    [],
  );
}
